/**
 * Document metadata applied once the application/funnel resolves (NX-11851).
 *
 * The SPA shell ships no `<title>`, so a single generic title is never served
 * for every brand. Title and description come from the configurable SEO
 * metadata (NX-11852): the funnel's `seo_json` overrides the application's,
 * both supporting `{token|fallback}` placeholders, with the brand name as the
 * last-resort title.
 *
 * This module owns how the resolved values reach the document so the same
 * resolution can later be pre-rendered at the edge.
 */
import {
  coerceSeoConfig,
  resolveSeoMetadata,
  type ResolvedSeoMetadata,
} from "@nextday/funnel-ui/seo";

import type { ResolvedApplication, ResolvedFunnel } from "@src/funnel/useApplicationContext";

export type DocumentMetadata = ResolvedSeoMetadata;

export const resolveDocumentMetadata = (
  application: ResolvedApplication,
  funnel: ResolvedFunnel | null,
  inletSlug?: string | null,
): DocumentMetadata =>
  resolveSeoMetadata({
    funnel: coerceSeoConfig(funnel?.seo),
    application: coerceSeoConfig(application.seo),
    tokens: {
      brand_name: application.name,
      ...(funnel !== null && funnel.name.length > 0 ? { funnel_name: funnel.name } : {}),
      ...(inletSlug ? { inlet_slug: inletSlug } : {}),
    },
  });

const upsertMeta = (attribute: "name" | "property", key: string, content: string): void => {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

/** Canonical URL without the tracking/QA query string. */
const canonicalUrl = (): string => `${window.location.origin}${window.location.pathname}`;

export const applyDocumentMetadata = (
  application: ResolvedApplication,
  funnel: ResolvedFunnel | null,
  inletSlug?: string | null,
): void => {
  const { title, description } = resolveDocumentMetadata(application, funnel, inletSlug);

  document.title = title;
  document.documentElement.lang = application.defaultLocale || "en";

  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:url", canonicalUrl());
  upsertMeta("name", "twitter:card", "summary");

  if (description !== null) {
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:description", description);
  }
};
